import {
  type QuadNumber,
  sqrtRational,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  addR,
  divR,
  intR,
  isZeroR,
  mulR,
  type Rational,
  signR,
  subR,
} from "@/lib/tool-kit/math/rational";

/**
 * Distance, midpoint, section point and endpoint for points in 2D or 3D — exact.
 *
 * Coordinates are rationals, so the distance between (1, 2) and (4, 6) is 5 and between
 * (0, 0) and (1, 1) is √2, never 1.41421356….
 */

export type Point = Rational[];

const TWO = intR(2);

export function differences(p: Point, q: Point): Rational[] {
  return q.map((x, i) => subR(x, p[i]));
}

/** The sum of squared coordinate differences: the number under the root. */
export function squaredDistance(p: Point, q: Point): Rational {
  return differences(p, q).reduce((sum, d) => addR(sum, mulR(d, d)), intR(0));
}

export function distance(p: Point, q: Point): QuadNumber {
  return sqrtRational(squaredDistance(p, q));
}

export function midpoint(p: Point, q: Point): Point {
  return p.map((x, i) => divR(addR(x, q[i]), TWO));
}

/** The other endpoint of a segment with one endpoint `p` and midpoint `m`: 2m − p. */
export function endpointFromMidpoint(p: Point, m: Point): Point {
  return m.map((x, i) => subR(mulR(TWO, x), p[i]));
}

export type SectionError = "not-positive" | "equal-external";

/**
 * The point that divides the segment from p to q in the ratio m : n.
 *
 * Internally: (n·p + m·q) / (m + n). Externally: (m·q − n·p) / (m − n), a point on the
 * line outside the segment: beyond q when m > n, beyond p when m < n (`externalSide`).
 */
export function sectionPoint(
  p: Point,
  q: Point,
  m: Rational,
  n: Rational,
  kind: "internal" | "external"
): Point | SectionError {
  if (signR(m) <= 0 || signR(n) <= 0) {
    return "not-positive";
  }
  if (kind === "internal") {
    const total = addR(m, n);
    return p.map((x, i) => divR(addR(mulR(n, x), mulR(m, q[i])), total));
  }
  const gap = subR(m, n);
  if (isZeroR(gap)) {
    return "equal-external";
  }
  return p.map((x, i) => divR(subR(mulR(m, q[i]), mulR(n, x)), gap));
}

/** For an external division, which end the point lies beyond. */
export function externalSide(m: Rational, n: Rational): "q" | "p" {
  return signR(subR(m, n)) > 0 ? "q" : "p";
}

export function isSamePoint(p: Point, q: Point): boolean {
  return p.every((x, i) => isZeroR(subR(x, q[i])));
}
